/**
 * Flight join codes: six common four-letter words, not six random
 * alphanumeric characters -- shouted across a table and typed on a phone
 * keyboard without anyone reaching for the "0 or O?" question. Modelled on
 * dohhh's lobby-code pattern (../dohhh packages/engine/src/joincode.ts),
 * simplified for proef: the code is looked up directly as a D1 row key, so
 * there's no need for dohhh's bit-packed integer round-trip (that exists
 * there to derive a hashed relay topic for its peer-to-peer transport).
 *
 * The wordlist is exactly 256 entries (2^8) so one random byte maps to
 * exactly one word with no bias and no partial-bit packing. The generator
 * itself (needs a CSPRNG) lives at the call site (apps/edge/src/flight.ts) —
 * this package is pure logic, no platform I/O.
 */

export const WORDS_PER_JOIN_CODE = 6;
export const JOIN_CODE_SEPARATOR = "-";

/** Exactly 256 words, 4 letters each, common and easy to say and spell.
 * Frozen: reordering doesn't break existing codes (lookup is by word
 * membership, not position), but adding/removing a word changes the
 * generator's output distribution, so treat this as append-only. */
export const JOIN_CODE_WORDLIST: readonly string[] = [
  "acid",
  "acre",
  "aged",
  "airy",
  "aloe",
  "amid",
  "anew",
  "apex",
  "arch",
  "area",
  "army",
  "atom",
  "aunt",
  "auto",
  "away",
  "axis",
  "baby",
  "back",
  "bake",
  "ball",
  "band",
  "bank",
  "barn",
  "base",
  "bath",
  "beam",
  "bean",
  "bear",
  "beat",
  "bell",
  "belt",
  "bend",
  "best",
  "bike",
  "bird",
  "bite",
  "blue",
  "boat",
  "body",
  "bold",
  "bolt",
  "bone",
  "book",
  "boot",
  "born",
  "boss",
  "bowl",
  "brew",
  "buck",
  "bulb",
  "bulk",
  "bull",
  "burn",
  "bush",
  "cafe",
  "cake",
  "calm",
  "camp",
  "card",
  "care",
  "cart",
  "cash",
  "cave",
  "cell",
  "chef",
  "chip",
  "city",
  "clam",
  "claw",
  "clay",
  "clip",
  "club",
  "coal",
  "coat",
  "code",
  "coin",
  "cold",
  "comb",
  "cone",
  "cook",
  "cool",
  "copy",
  "cord",
  "cork",
  "corn",
  "cost",
  "crab",
  "crew",
  "crop",
  "crow",
  "cube",
  "cuff",
  "curb",
  "curl",
  "dart",
  "dash",
  "dawn",
  "deck",
  "deep",
  "deer",
  "desk",
  "dice",
  "dish",
  "dock",
  "doll",
  "dome",
  "dose",
  "down",
  "drop",
  "drum",
  "duck",
  "dust",
  "duty",
  "each",
  "earn",
  "east",
  "easy",
  "echo",
  "edge",
  "epic",
  "exam",
  "face",
  "fact",
  "fade",
  "fair",
  "fall",
  "fame",
  "farm",
  "fast",
  "fate",
  "fawn",
  "feed",
  "file",
  "film",
  "find",
  "fine",
  "fire",
  "fish",
  "flag",
  "flat",
  "flow",
  "folk",
  "fond",
  "font",
  "food",
  "fork",
  "form",
  "fort",
  "free",
  "frog",
  "fuel",
  "full",
  "fund",
  "gain",
  "gala",
  "game",
  "gate",
  "gaze",
  "gear",
  "gift",
  "girl",
  "glad",
  "glow",
  "glue",
  "goat",
  "gold",
  "golf",
  "good",
  "gown",
  "grab",
  "gray",
  "grid",
  "grip",
  "grow",
  "gulf",
  "gust",
  "hail",
  "half",
  "hall",
  "halo",
  "hand",
  "hard",
  "harp",
  "hash",
  "haze",
  "head",
  "heal",
  "heap",
  "heat",
  "heel",
  "herb",
  "herd",
  "hero",
  "hide",
  "high",
  "hike",
  "hill",
  "hint",
  "hive",
  "hold",
  "holy",
  "home",
  "hood",
  "hoof",
  "hope",
  "horn",
  "host",
  "hour",
  "hunt",
  "hush",
  "husk",
  "icon",
  "idle",
  "inch",
  "iris",
  "iron",
  "itch",
  "item",
  "jade",
  "jazz",
  "jolt",
  "jump",
  "junk",
  "jury",
  "jute",
  "keep",
  "kelp",
  "kind",
  "king",
  "kite",
  "knee",
  "knot",
  "lace",
  "lake",
  "lamb",
  "lamp",
  "land",
  "lane",
  "leaf",
  "leap",
  "leek",
  "lens",
  "lily",
  "lime",
  "line",
  "link",
  "lion",
  "list",
  "loaf",
  "loan",
  "lobe",
  "lock",
  "loft",
  "long",
  "loop",
  "lord",
];

const WORDS = new Set(JOIN_CODE_WORDLIST);

/** Lowercase with diacritics stripped: a phone keyboard that autocorrects
 * "acid" to "ácid" or capitalises the first letter still means the word. */
function foldJoinCodeText(input: string): string {
  return input
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase();
}

/** The words of a typed or pasted code, split on any run of non-letters
 * (spaces, dashes, dots, commas) -- known or not, nothing is dropped. */
export function splitJoinCodeWords(input: string): string[] {
  return foldJoinCodeText(input)
    .split(/[^a-z]+/)
    .filter((part) => part.length > 0);
}

export function isJoinCodeWord(input: string): boolean {
  return WORDS.has(foldJoinCodeText(input.trim()));
}

/** `null` for anything that isn't six known words -- forgiving of spaces,
 * capitals, accents and stray punctuation the way people actually type a code. */
export function normalizeJoinCode(input: string): string | null {
  const words = splitJoinCodeWords(input);
  if (words.length !== WORDS_PER_JOIN_CODE) return null;
  if (words.some((w) => !WORDS.has(w))) return null;
  return words.join(JOIN_CODE_SEPARATOR);
}

export function isValidJoinCode(input: string): boolean {
  return normalizeJoinCode(input) !== null;
}

/** Completions for one word of a code being typed on a phone keyboard. */
export function completeJoinCodeWord(prefix: string, limit = 6): string[] {
  const needle = foldJoinCodeText(prefix.trim());
  if (needle.length === 0) return [];
  const out: string[] = [];
  for (const word of JOIN_CODE_WORDLIST) {
    if (word.startsWith(needle)) {
      out.push(word);
      if (out.length >= limit) break;
    }
  }
  return out;
}
